import React from "react";

/**
 * LogoMarquee — infinite horizontal scroller of brand logos.
 *
 * Props:
 *   eyebrow: string  — small kicker line above the title
 *   title:   string  — main headline
 *   subtitle:string  — descriptive paragraph
 *   logos: Array<{ name: string; src: string }>
 *   theme:  "dark" | "light" (defaults to "dark")
 *
 * The logos are DUPLICATED and the track is animated left-to-left so the
 * loop appears seamless. Brand colors are neutralized to soft white on dark
 * and to a low-contrast gray on light — this keeps the wall discreet and
 * lets it work with any mixed set of client logos.
 */
export default function LogoMarquee({
  eyebrow = "◈ Escolas que caminham com a Orion",
  title = "Clientes & Parceiros",
  subtitle = "Instituições que confiaram na curadoria da Orion para transformar viagens em experiência formativa.",
  logos = [],
  theme = "dark",
}) {
  if (!logos.length) return null;
  const doubled = [...logos, ...logos];
  const isDark = theme === "dark";

  return (
    <section
      className={`relative overflow-hidden ${isDark ? "bg-[#0d0526] text-white" : "bg-[#FAF7F0] text-orion-purple"}`}
      data-testid="logo-marquee"
    >
      <div className="container-orion py-16 md:py-20">
        <div className="max-w-2xl mb-10">
          <div className={`text-[11px] uppercase tracking-[0.32em] font-semibold mb-3 ${isDark ? "text-orion-gold" : "text-orion-orange"}`}>
            {eyebrow}
          </div>
          <h2 className={`font-serif text-3xl md:text-4xl leading-tight ${isDark ? "" : "text-orion-purple"}`}>
            {title}
          </h2>
          {subtitle && (
            <p className={`mt-3 text-sm md:text-base leading-relaxed ${isDark ? "text-white/75" : "text-[#594A63]"}`}>
              {subtitle}
            </p>
          )}
        </div>

        <div className="lm-marquee" aria-hidden={false}>
          <div className="lm-track">
            {doubled.map((p, i) => (
              <div key={`${p.name}-${i}`} className="lm-item" title={p.name} aria-label={p.name}>
                <div className={`lm-disc ${isDark ? "lm-disc-dark" : "lm-disc-light"}`}>
                  <img src={p.src} alt={p.name} loading="lazy" decoding="async" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .lm-marquee {
          position: relative;
          overflow: hidden;
          mask-image: linear-gradient(to right, transparent, black 8%, black 92%, transparent);
          -webkit-mask-image: linear-gradient(to right, transparent, black 8%, black 92%, transparent);
        }
        .lm-track {
          display: flex;
          gap: 48px;
          width: max-content;
          animation: lm-scroll 42s linear infinite;
        }
        .lm-track:hover { animation-play-state: paused; }
        .lm-item {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .lm-disc {
          width: 120px;
          height: 120px;
          border-radius: 999px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          transition: transform 300ms ease, box-shadow 300ms ease;
        }
        .lm-disc-dark {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.08);
          box-shadow: inset 0 0 40px -20px rgba(255, 203, 107, 0.35);
        }
        .lm-disc-light {
          background: #ffffff;
          border: 1px solid #EFEAE0;
          box-shadow: 0 6px 22px -12px rgba(44, 17, 57, 0.12);
        }
        .lm-disc img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
        }
        .lm-disc-dark img {
          filter: grayscale(1) brightness(2.4) contrast(1.05);
          opacity: 0.85;
          transition: filter 300ms ease, opacity 300ms ease;
        }
        .lm-item:hover .lm-disc { transform: translateY(-4px); }
        .lm-item:hover .lm-disc-dark { box-shadow: 0 12px 34px -14px rgba(255, 203, 107, 0.55), inset 0 0 42px -18px rgba(255, 203, 107, 0.6); }
        .lm-item:hover .lm-disc-dark img { filter: none; opacity: 1; }
        .lm-item:hover .lm-disc-light { box-shadow: 0 14px 34px -14px rgba(44, 17, 57, 0.22); border-color: var(--orion-orange, #FF7A2E); }
        @keyframes lm-scroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        @media (max-width: 640px) {
          .lm-disc { width: 92px; height: 92px; padding: 12px; }
          .lm-track { gap: 28px; animation-duration: 32s; }
        }
      `}</style>
    </section>
  );
}

// Exportable curated logo lists — one place to maintain the sets.
export const ESCOLAS_LOGOS = [
  { name: "Colégio Dom Henrique",                src: "/img/escolas/dom-henrique.png" },
  { name: "Centro Educacional Delphim Nogueira", src: "/img/escolas/delphim-nogueira.jpg" },
  { name: "Objetivo",                            src: "/img/escolas/objetivo.jpg" },
];
